import React, { useState, useCallback } from 'react'
import { gql, useMutation } from '@apollo/client'
import { useHistory, useLocation } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useForm, FormProvider } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import Container from '@material-ui/core/Container'
import LinearProgress from '@material-ui/core/LinearProgress'
import { validationSchema } from './validation'
import makeDefaultValues, {
  splitRecurrenceOccurrences,
} from './makeDefaultValues'
import buildUpdateVariables from './buildUpdateVariables'
import getUpdateImpact from './getUpdateImpact'
import UpdateReasonDialog from './update-reason-dialog'
import { useStyles } from './styles'
import FormSectionContainer from './form-section-container'
import EditEventSectionNav, {
  EDIT_EVENT_SECTIONS,
  getActiveSectionIndex,
  getSectionIdFromHash,
} from './edit-event-section-nav'
import EditEventPagination from './edit-event-pagination'
import EventNameAndTags from './form-sections/event-name-and-tags'
import EventLocation from './form-sections/event-location'
import ScheduleSessions from './form-sections/schedule-sessions'
import RegistrationCloseDay from './form-sections/registration-close-day'
import RegistrationOpenDateTime from './form-sections/registration-open-datetime'
import MaxAttendance from './form-sections/max-attendance'
import AccessCode from './form-sections/access-code'
import EventDescription from './form-sections/event-description'
import FeaturedImage from './form-sections/featured-image'
import Fees from './form-sections/fees'
import ExternalRegistrationUrl from './form-sections/event-external-registration-url'
import SessionList from './form-sections/session-list'
import Unlisted from './form-sections/unlisted'
import { isRecurringPayment } from 'utils/groupEventUtils'
import AssistantCoaches from './form-sections/assistant-coaches'
import ArrowBackOutlinedIcon from 'components/material-symbol/icons/ArrowBack'
import AgeGroupSelect from 'components/age-group-select'
import {
  getOfferingsPath,
  OFFERINGS_TAB,
} from 'pages/pga-coach/bookings/offerings/routes'
import { getFirstErrorMessage } from 'pages/pga-coach/bookings/events/form-validation-utils'
import { useFeatureFlags, flags } from 'lib/feature-flags'
import { RecurringDatesInput } from 'components/group-events/recurring-schedule'
import RecurringChip from 'components/group-events/recurring-chip'

const UPDATE_GROUP_EVENT = gql`
  mutation UpdateGroupEvent(
    $id: ID!
    $values: GroupEventInput!
    $reason: String
  ) {
    updateGroupEvent(id: $id, values: $values, reason: $reason) {
      success
      message
    }
  }
`

const UPDATE_RECURRING_GROUP_EVENT = gql`
  mutation UpdateRecurringGroupEvent(
    $id: ID!
    $values: GroupEventInput!
    $occurrences: [GroupEventOccurrenceInput!]!
    $reason: String
  ) {
    updateRecurringGroupEvent(
      id: $id
      values: $values
      occurrences: $occurrences
      reason: $reason
    ) {
      success
      message
    }
  }
`

const EditEventLayout = ({
  event,
  coachFacilities,
  paymentAccounts,
  embedInSchedule = false,
  onSaveSuccess,
  onCancel: onCancelEmbed,
}) => {
  const isAcademyEvent = event?.academy?.id
  const isPgaHopeEvent = event?.tags.includes('PGA HOPE')
  const [isRecurringInstancesEnabled] = useFeatureFlags([
    flags.FLAG_FEAT_GROUP_EVENT_RECURRING_INSTANCES,
  ])
  const editRecurrence = Boolean(
    isRecurringInstancesEnabled && event?.recurrence,
  )
  const { pastDateCount } = editRecurrence
    ? splitRecurrenceOccurrences(event)
    : { pastDateCount: 0 }

  const eligibleFacilities = isAcademyEvent
    ? event.academy?.facilities
    : coachFacilities

  const { enqueueSnackbar } = useSnackbar()
  const classes = useStyles()
  const history = useHistory()
  const { hash } = useLocation()
  const [isUpdateReasonModalOpen, setIsUpdateReasonModalOpen] = useState(false)
  const [updateReason, setUpdateReason] = useState('')
  const [updateImpact, setUpdateImpact] = useState({
    hasImpact: false,
    impacts: [],
  })
  const [activeSection, setActiveSection] = useState(() =>
    getSectionIdFromHash(hash),
  )
  const activeStepIndex = getActiveSectionIndex(activeSection)
  const isFirstStep = activeStepIndex === 0
  const isLastStep = activeStepIndex === EDIT_EVENT_SECTIONS.length - 1
  const currentSection = EDIT_EVENT_SECTIONS[activeStepIndex]

  const scrollToFormTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  const handleSectionClick = useCallback(
    sectionId => {
      setActiveSection(sectionId)
      scrollToFormTop()
    },
    [scrollToFormTop],
  )

  const getStepFields = useCallback(
    section => {
      if (section.id === 'edit-section-basics') {
        return ['title', 'tags']
      }

      if (section.id === 'edit-section-location') {
        return ['locationId']
      }

      if (section.id === 'edit-section-schedule') {
        const scheduleFields = ['sessions']

        if (event.externalRegistrationUrl) {
          scheduleFields.push('externalRegistrationUrl')
        }

        if (isRecurringPayment(event)) {
          return scheduleFields.filter(field => field !== 'sessions')
        }

        return scheduleFields
      }

      if (section.id === 'edit-section-registration') {
        const registrationFields = ['maxAttendance', 'accessCode']

        if (!isPgaHopeEvent) {
          registrationFields.push('price', 'paymentAccountId')
        }

        if (!event.externalRegistrationUrl) {
          registrationFields.push(
            'hoursBeforeRegistrationCloses',
            'registrationOpenDate',
            'registrationOpenTime',
          )
        }

        return registrationFields
      }

      if (section.id === 'edit-section-media') {
        return ['featuredImageUrl', 'unlisted']
      }

      return section.fields || []
    },
    [event, isPgaHopeEvent],
  )

  const handlePrevious = useCallback(() => {
    if (!isFirstStep) {
      setActiveSection(EDIT_EVENT_SECTIONS[activeStepIndex - 1].id)
      scrollToFormTop()
    }
  }, [activeStepIndex, isFirstStep, scrollToFormTop])

  const handleUpdateResult = result => {
    if (result.success) {
      enqueueSnackbar('Event updated successfully', {
        variant: 'success',
      })
      if (embedInSchedule) {
        onSaveSuccess?.()
      } else {
        setTimeout(() => {
          history.push(getOfferingsPath(OFFERINGS_TAB.GROUP_EVENTS))
        }, 1000)
      }
    } else {
      enqueueSnackbar(result.message, {
        variant: 'error',
      })
    }
  }

  const [updateGroupEvent, { loading: updateLoading }] = useMutation(
    UPDATE_GROUP_EVENT,
    { onCompleted: data => handleUpdateResult(data.updateGroupEvent) },
  )
  const [
    updateRecurringGroupEvent,
    { loading: updateRecurringLoading },
  ] = useMutation(UPDATE_RECURRING_GROUP_EVENT, {
    onCompleted: data => handleUpdateResult(data.updateRecurringGroupEvent),
    onError: error => enqueueSnackbar(error.message, { variant: 'error' }),
  })
  const loading = updateLoading || updateRecurringLoading

  const form = useForm({
    defaultValues: makeDefaultValues(event, { editRecurrence }),
    shouldUnregister: false,
    resolver: yupResolver(
      validationSchema(eligibleFacilities, paymentAccounts, event, {
        editRecurrence,
      }),
    ),
  })

  const {
    formState: { dirtyFields },
    watch,
    trigger,
  } = form

  const navigateToSectionWithError = useCallback(
    formErrors => {
      const errorFields = Object.keys(formErrors)
      const sectionWithError = EDIT_EVENT_SECTIONS.find(section => {
        const fields = getStepFields(section)
        return fields.some(field => errorFields.includes(field))
      })

      if (sectionWithError) {
        setActiveSection(sectionWithError.id)
        scrollToFormTop()
      }
    },
    [getStepFields, scrollToFormTop],
  )

  const showFirstValidationError = useCallback(
    formErrors => {
      const errorMessage = getFirstErrorMessage(formErrors)

      if (errorMessage) {
        enqueueSnackbar(errorMessage, { variant: 'error' })
      }
    },
    [enqueueSnackbar],
  )

  const handleNext = useCallback(async () => {
    if (isLastStep) return

    const fields = getStepFields(currentSection)
    const isValid = fields.length ? await trigger(fields) : true

    if (isValid) {
      setActiveSection(EDIT_EVENT_SECTIONS[activeStepIndex + 1].id)
      scrollToFormTop()
      return
    }

    const formErrors = form.formState.errors
    showFirstValidationError(formErrors)
  }, [
    activeStepIndex,
    currentSection,
    form.formState.errors,
    getStepFields,
    isLastStep,
    scrollToFormTop,
    showFirstValidationError,
    trigger,
  ])

  const selectedFacilityId = watch('locationId')
  const selectedFacility = coachFacilities?.find(
    facility => facility.id === selectedFacilityId,
  )

  const handleSaveClick = async e => {
    e.preventDefault()

    const isValid = await form.trigger()

    if (!isValid) {
      const formErrors = form.formState.errors
      navigateToSectionWithError(formErrors)
      showFirstValidationError(formErrors)
      return
    }

    if (editRecurrence) {
      const formValues = form.getValues()
      const impact = getUpdateImpact({ event, formValues, editRecurrence })

      if (impact.hasImpact) {
        setUpdateImpact(impact)
        setIsUpdateReasonModalOpen(true)
        return
      }

      form.handleSubmit(onSubmit)(e)
      return
    }

    if (dirtyFields.locationId || dirtyFields.sessions) {
      setIsUpdateReasonModalOpen(true)
      return
    }

    form.handleSubmit(onSubmit)(e)
  }

  const onUpdateReasonChange = event => {
    setUpdateReason(event.target.value)
  }

  const handleUpdateDialogClose = () => {
    setIsUpdateReasonModalOpen(false)
    setUpdateReason('')
    setUpdateImpact({ hasImpact: false, impacts: [] })
  }

  const onSubmit = async formValues => {
    const variables = buildUpdateVariables({
      formValues,
      event,
      eligibleFacilities,
      editRecurrence,
      updateReason,
    })

    if (editRecurrence) {
      return updateRecurringGroupEvent({ variables })
    }

    return updateGroupEvent({ variables })
  }

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'edit-section-basics':
        return (
          <FormSectionContainer
            id={currentSection.id}
            title={currentSection.pageTitle}
          >
            <EventNameAndTags />
            <Box className={classes.fieldGroup}>
              <AgeGroupSelect className={classes.fieldInput} />
            </Box>
            <EventDescription />
          </FormSectionContainer>
        )
      case 'edit-section-location':
        return (
          <FormSectionContainer
            id={currentSection.id}
            title={currentSection.pageTitle}
          >
            <EventLocation event={event} coachFacilities={eligibleFacilities} />
          </FormSectionContainer>
        )
      case 'edit-section-schedule':
        return (
          <FormSectionContainer
            id={currentSection.id}
            title={currentSection.pageTitle}
          >
            <AssistantCoaches event={event} />
            {event.externalRegistrationUrl ? (
              <Box className={classes.fieldGroup}>
                <ExternalRegistrationUrl />
              </Box>
            ) : null}
            {isRecurringPayment(event) ? (
              <Box className={classes.fieldGroup}>
                <Typography className={classes.fieldHelper}>
                  Subscription session dates/times cannot be edited. To make
                  changes, this offering will need to be duplicated and
                  cancelled.
                </Typography>
                <SessionList
                  event={event}
                  timezone={selectedFacility?.timezone}
                />
              </Box>
            ) : editRecurrence ? (
              <>
                <Box className={classes.fieldGroup}>
                  <RecurringChip />
                </Box>
                <RecurringDatesInput
                  timezone={selectedFacility?.timezone}
                  showBuilder={false}
                  pastDateCount={pastDateCount}
                />
              </>
            ) : (
              <ScheduleSessions timezone={selectedFacility?.timezone} />
            )}
          </FormSectionContainer>
        )
      case 'edit-section-registration':
        return (
          <FormSectionContainer
            id={currentSection.id}
            title={currentSection.pageTitle}
          >
            {!event.externalRegistrationUrl ? (
              <RegistrationOpenDateTime />
            ) : null}
            <RegistrationCloseDay
              eventStartDateTime={event.startDateTime}
              isExternalRegistration={!!event.externalRegistrationUrl}
              timezone={selectedFacility?.timezone || event.timezone}
            />
            <MaxAttendance />
            <AccessCode />
            {!isPgaHopeEvent ? (
              <Box className={classes.fieldGroup}>
                <Fees event={event} paymentAccounts={paymentAccounts} />
              </Box>
            ) : null}
          </FormSectionContainer>
        )
      case 'edit-section-media':
        return (
          <FormSectionContainer
            id={currentSection.id}
            title={currentSection.pageTitle}
          >
            <FeaturedImage />
            <Unlisted
              showPreview
              coachFacilities={eligibleFacilities}
              coachName={event.coach?.name}
            />
          </FormSectionContainer>
        )
      default:
        return null
    }
  }

  return (
    <Box>
      <FormProvider {...form}>
        <form onSubmit={handleSaveClick}>
          <Box className={classes.actionBar}>
            <Button
              type="button"
              className={classes.cancelAction}
              startIcon={<ArrowBackOutlinedIcon fontSize="small" />}
              onClick={
                embedInSchedule && onCancelEmbed
                  ? onCancelEmbed
                  : () => history.goBack()
              }
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              className={classes.saveAction}
            >
              Save
            </Button>
          </Box>

          {loading ? (
            <LinearProgress color="primary" className={classes.loadingStyles} />
          ) : null}
          <Container maxWidth="lg">
            <Box pt={4} pb={3}>
              <Box className={classes.layoutGrid}>
                <Box className={classes.navColumn}>
                  <EditEventSectionNav
                    activeSection={activeSection}
                    onSectionClick={handleSectionClick}
                  />
                </Box>

                <Box className={classes.formColumn}>
                  {renderActiveSection()}
                  <EditEventPagination
                    isFirstStep={isFirstStep}
                    isLastStep={isLastStep}
                    onPrevious={handlePrevious}
                    onNext={handleNext}
                  />
                </Box>
              </Box>
            </Box>
          </Container>
        </form>
      </FormProvider>
      <UpdateReasonDialog
        open={isUpdateReasonModalOpen}
        impacts={updateImpact.impacts}
        timezone={event?.timezone}
        updateReason={updateReason}
        onUpdateReasonChange={onUpdateReasonChange}
        onConfirm={form.handleSubmit(onSubmit)}
        onCancel={handleUpdateDialogClose}
      />
    </Box>
  )
}

export default EditEventLayout
